
import {
  computed, defineComponent, PropType, useSlots,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PaymentBlock from '@/components/billing/shared/PaymentBlock.vue';
import { ActionResult } from '@/composables/billing/billingApi';
import { PaymentMethodItem } from '@/types/billing-types';

type PaymentMode = 'existing' | 'new';

export interface ReviewAndPayLine {
  label: string;
  value: string;
  /** Visual variant — drives a SCSS modifier on the row. */
  kind?: 'normal' | 'discount' | 'accent';
}

/**
 * Shared two-column « review + pay » card used by every billing checkout flow
 * (regularising an unpaid invoice, buying one-shot credits, changing plan,
 * etc.). The recap on the left is data-driven via props ; the payment column
 * delegates to {@link PaymentBlock} so the saved-card / new-card UX stays
 * consistent everywhere.
 *
 * The CTA is intentionally OUTSIDE this card — each parent dialog renders its
 * own footer with the right label / icon / handler. This keeps the card
 * itself stateless and reusable.
 */
export default defineComponent({
  name: 'ReviewAndPayCard',

  components: { PaymentBlock },

  props: {
    // ─── LEFT column (recap) ────────────────────────────────────────────
    recapTitle: {
      type: String,
      required: true,
    },

    recapSubtitle: {
      type: String,
      default: '',
    },

    /** FontAwesome class. Defaults to `fa-regular fa-receipt`. */
    recapIcon: {
      type: String,
      default: '',
    },

    recapLines: {
      type: Array as PropType<ReviewAndPayLine[]>,
      default: () => [],
    },

    totalLabel: {
      type: String,
      required: true,
    },

    /** Pre-formatted (e.g. `"11,99 €"`). The card is display-only. */
    totalValue: {
      type: String,
      required: true,
    },

    // ─── RIGHT column (payment) — forwarded to PaymentBlock ─────────────
    defaultCard: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    allCards: {
      type: Array as PropType<PaymentMethodItem[]>,
      default: () => [],
    },

    paymentMode: {
      type: String as PropType<PaymentMode>,
      default: 'existing',
    },

    paymentMethodId: {
      type: String as PropType<string | null>,
      default: null,
    },

    amount: {
      type: Number,
      required: true,
    },

    currency: {
      type: String as PropType<'EUR' | 'USD'>,
      default: 'EUR',
    },

    dropinSubmitHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    dropinDetailsHandler: {
      type: Function as PropType<(state: Record<string, any>) => Promise<ActionResult>>,
      required: true,
    },

    /**
     * Adyen payment methods exposed in the Drop-in when the user adds a new
     * method. Forwarded to {@link PaymentBlock}. See its docstring for the
     * recommended values per flow.
     */
    allowedMethods: {
      type: Array as PropType<string[]>,
      default: () => ['scheme'],
    },

    paymentError: {
      type: String as PropType<string | null>,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // ─── In-card CTA ────────────────────────────────────────────────────
    /** When empty, no CTA is rendered. The CTA stays hidden when the user
     *  is in the new-card Drop-in flow (the Drop-in has its own pay button). */
    ctaLabel: {
      type: String,
      default: '',
    },

    ctaIcon: {
      type: String,
      default: '',
    },

    ctaSubmitting: {
      type: Boolean,
      default: false,
    },

    ctaDisabled: {
      type: Boolean,
      default: false,
    },

    /** Label shown next to the spinner while submitting. Defaults to
     *  `billing.review.processing`. */
    ctaLoadingLabel: {
      type: String,
      default: '',
    },

    /** Reassurance text under the CTA (e.g. « Paiement sécurisé »). When
     *  empty, no trust line is shown. */
    trustText: {
      type: String,
      default: '',
    },
  },

  emits: [
    'update:paymentMode',
    'update:paymentMethodId',
    'dropin-success',
    'dropin-error',
    'cta-click',
  ],

  setup(props, { emit }) {
    const { t } = useI18n();
    const slots = useSlots();

    const hasPaymentContentSlot = computed(() => !!slots['payment-content']);

    // CTA visibility :
    //   - parent passes a non-empty label
    //   - AND we're either showing a custom payment-content slot (downgrade /
    //     Shopify — those flows never go through the Drop-in) OR the user is
    //     using their saved card (mode='existing').
    // When mode='new', the Adyen Drop-in renders its own pay button — hiding
    // ours avoids a confusing double CTA.
    const showCta = computed(() => (
      !!props.ctaLabel
      && (hasPaymentContentSlot.value || props.paymentMode === 'existing')
    ));

    const onPaymentModeChange = (mode: PaymentMode) => emit('update:paymentMode', mode);
    const onPaymentMethodIdChange = (id: string | null) => emit('update:paymentMethodId', id);
    const onDropinSuccess = () => emit('dropin-success');
    const onDropinError = (err: string) => emit('dropin-error', err);
    const onCtaClick = () => emit('cta-click');

    return {
      t,
      hasPaymentContentSlot,
      showCta,
      onPaymentModeChange,
      onPaymentMethodIdChange,
      onDropinSuccess,
      onDropinError,
      onCtaClick,
    };
  },
});
